import React, { useState } from 'react';
import { CheckIcon, DownloadIcon, PlugIcon } from 'lucide-react';
import { CompanySettings, Employee } from '../../types/pro';
import { PageHeader } from '../../components/pro/PageHeader';
interface ProSettingsProps {
  company: CompanySettings;
  employees: Employee[];
  onSave: (settings: CompanySettings) => void;
  /** Re-runs the import from the old system. Returns records brought across. */
  onReimportLegacy: () => Promise<number>;
}
type Tab = 'virksomhed' | 'brugere' | 'roller' | 'tilbud' | 'faktura' | 'notifikationer' | 'integrationer';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'virksomhed',
  label: 'Virksomhed'
}, {
  id: 'brugere',
  label: 'Brugere'
}, {
  id: 'roller',
  label: 'Roller og rettigheder'
}, {
  id: 'tilbud',
  label: 'Tilbud'
}, {
  id: 'faktura',
  label: 'Faktura'
}, {
  id: 'notifikationer',
  label: 'Notifikationer'
}, {
  id: 'integrationer',
  label: 'Integrationer'
}];
const PERMISSIONS = [{
  label: 'Se og redigere alle opgaver',
  admin: true,
  pm: true,
  worker: false
}, {
  label: 'Oprette og sende tilbud',
  admin: true,
  pm: true,
  worker: false
}, {
  label: 'Se priser og avance',
  admin: true,
  pm: true,
  worker: false
}, {
  label: 'Registrere egne timer',
  admin: true,
  pm: true,
  worker: true
}, {
  label: 'Tilføje materialer på opgave',
  admin: true,
  pm: true,
  worker: true
}, {
  label: 'Oprette og sende fakturaer',
  admin: true,
  pm: false,
  worker: false
}, {
  label: 'Administrere brugere',
  admin: true,
  pm: false,
  worker: false
}];
const NOTIFICATIONS = [{
  id: 'new-lead',
  label: 'Nyt lead modtaget',
  hint: 'E-mail med det samme'
}, {
  id: 'quote-seen',
  label: 'Kunden har set et tilbud',
  hint: 'E-mail'
}, {
  id: 'quote-accepted',
  label: 'Tilbud accepteret',
  hint: 'E-mail og notifikation'
}, {
  id: 'invoice-overdue',
  label: 'Faktura forfalden',
  hint: 'Daglig opsamling'
}, {
  id: 'job-done',
  label: 'Opgave afsluttet',
  hint: 'Notifikation'
}];
const INTEGRATIONS = [{
  name: 'e-conomic',
  description: 'Send fakturaer direkte til bogholderiet'
}, {
  name: 'Dinero',
  description: 'Synkronisér kunder og fakturaer'
}, {
  name: 'MobilePay Erhverv',
  description: 'Betalingslink på fakturaen'
}, {
  name: 'Google Kalender',
  description: 'Vis opgaver i medarbejdernes kalender'
}];
export function ProSettings({
  company,
  employees,
  onSave,
  onReimportLegacy
}: ProSettingsProps) {
  const [tab, setTab] = useState<Tab>('virksomhed');
  const [draft, setDraft] = useState(company);
  const [saved, setSaved] = useState(false);
  const [importState, setImportState] = useState<'idle' | 'running' | 'done' | 'empty' | 'error'>('idle');
  const [importCount, setImportCount] = useState(0);
  const runImport = async () => {
    if (!window.confirm('Hent kunder og opgaver fra det gamle system?\n\nAlt i Kunder, Opgaver, Leads, Tilbud og Fakturaer her bliver erstattet. Det gamle system røres ikke.')) return;
    setImportState('running');
    try {
      const count = await onReimportLegacy();
      setImportCount(count);
      setImportState(count > 0 ? 'done' : 'empty');
    } catch (error) {
      console.error('Error importing from the previous system:', error);
      setImportState('error');
    }
  };
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    'new-lead': true,
    'quote-seen': true,
    'quote-accepted': true,
    'invoice-overdue': true,
    'job-done': false
  });
  const set = <K extends keyof CompanySettings,>(key: K, value: CompanySettings[K]) => {
    setDraft(current => ({
      ...current,
      [key]: value
    }));
    setSaved(false);
  };
  const save = () => {
    onSave(draft);
    setSaved(true);
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const labelText = 'mb-1.5 block text-sm font-medium text-stone-700';
  const card = 'rounded-xl border border-stone-200 bg-white p-5';
  return <div data-id="element-2082" data-mp="2r93dkh9ukoqh">
      <PageHeader title="Indstillinger" subtitle="Værdier herfra bruges som standard i tilbud og fakturaer" data-id="element-2083" data-mp="2r8jxl63rcaf3" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[220px_1fr]" data-id="element-2084" data-mp="302q7s2yepjxk">
        <nav aria-label="Indstillinger" className="lg:sticky lg:top-24 lg:self-start" data-id="element-2085" data-mp="3052rolnq64d9">
          <ul className="flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1 lg:flex-col lg:overflow-visible" data-id="element-2086" data-mp="2w26a8y9x8eaw">
            {TABS.map(item => <li key={item.id} className="lg:w-full" data-id="element-2087" data-mp="23dqx6haco627">
                <button onClick={() => setTab(item.id)} aria-current={tab === item.id ? 'page' : undefined} className={`w-full whitespace-nowrap rounded-lg px-3.5 py-2 text-left text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-600 hover:bg-stone-100'}`} data-id="element-2088" data-mp="2hcgxs6wdonvb">
                  {item.label}
                </button>
              </li>)}
          </ul>
        </nav>

        <div className="min-w-0" data-id="element-2089" data-mp="43o2kns6lm42">
          {tab === 'virksomhed' && <section className={card} data-id="element-2090" data-mp="3mcyc44ets8nq">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-2091" data-mp="3ragne1gbezyw">
                <label className="block sm:col-span-2" data-id="element-2092" data-mp="3raqghxwnpn8t">
                  <span className={labelText} data-id="element-2093" data-mp="iuestre4kd52">Virksomhedsnavn</span>
                  <input value={draft.name} onChange={e => set('name', e.target.value)} className={field} data-id="element-2094" data-mp="ntrrd9ecz8nh" />
                </label>
                <label className="block" data-id="element-2095" data-mp="e3onuf9m2caw">
                  <span className={labelText} data-id="element-2096" data-mp="e2nag454hdq0">CVR</span>
                  <input value={draft.cvr} onChange={e => set('cvr', e.target.value)} className={field} data-id="element-2097" data-mp="11tss8oumig2y" />
                </label>
                <label className="block" data-id="element-2098" data-mp="1ggrhcd60cyei">
                  <span className={labelText} data-id="element-2099" data-mp="1gfpulbfmqngy">Adresse</span>
                  <input value={draft.address} onChange={e => set('address', e.target.value)} className={field} data-id="element-2100" data-mp="1bgmve7mbzbgw" />
                </label>
                <label className="block" data-id="element-2101" data-mp="12ff2y66hx4uq">
                  <span className={labelText} data-id="element-2102" data-mp="12it45b3fb80l">Postnummer</span>
                  <input value={draft.postalCode} onChange={e => set('postalCode', e.target.value)} className={field} data-id="element-2103" data-mp="xi5diqj3wt6w" />
                </label>
                <label className="block" data-id="element-2104" data-mp="nru1sht6lfpg">
                  <span className={labelText} data-id="element-2105" data-mp="iszhfbfkfj38">By</span>
                  <input value={draft.city} onChange={e => set('city', e.target.value)} className={field} data-id="element-2106" data-mp="iuaih3hmmcqn" />
                </label>
                <label className="block" data-id="element-2107" data-mp="9u5dezzwnqfn">
                  <span className={labelText} data-id="element-2108" data-mp="2lfxr48n6r7er">Telefon</span>
                  <input value={draft.phone} onChange={e => set('phone', e.target.value)} className={field} data-id="element-2109" data-mp="2lhh7s0cr7kh2" />
                </label>
                <label className="block" data-id="element-2110" data-mp="2zexk1shqe8gq">
                  <span className={labelText} data-id="element-2111" data-mp="2zdmf3pcjuuo1">E-mail</span>
                  <input value={draft.email} onChange={e => set('email', e.target.value)} className={field} data-id="element-2112" data-mp="22owuvsdg9pyw" />
                </label>
                <label className="block" data-id="element-2113" data-mp="2botdpdd5rum1">
                  <span className={labelText} data-id="element-2114" data-mp="2bo0fj0s9mdfi">Hjemmeside</span>
                  <input value={draft.website} onChange={e => set('website', e.target.value)} className={field} data-id="element-2115" data-mp="2ggbea0b9rt6h" />
                </label>
                <label className="block" data-id="element-2116" data-mp="1nyfveekdla4n">
                  <span className={labelText} data-id="element-2117" data-mp="1nxe8nctzyz73">Timepris (kr)</span>
                  <input type="number" value={draft.hourlyRate} onChange={e => set('hourlyRate', parseFloat(e.target.value) || 0)} className={field} data-id="element-2118" data-mp="hcybjnfijvgx" />
                </label>
                <label className="block" data-id="element-2119" data-mp="mdm1e5s0a69k">
                  <span className={labelText} data-id="element-2120" data-mp="10ar1zq8yacxf">Momssats (%)</span>
                  <input type="number" value={Math.round(draft.vatRate * 100)} onChange={e => set('vatRate', (parseFloat(e.target.value) || 0) / 100)} className={field} data-id="element-2121" data-mp="108xuy9ufdqow" />
                </label>
                <label className="block" data-id="element-2122" data-mp="1j10gnzlxbx60">
                  <span className={labelText} data-id="element-2123" data-mp="1j21qy1v48fmo">Betalingsbetingelser (dage)</span>
                  <input type="number" value={draft.paymentTermsDays} onChange={e => set('paymentTermsDays', parseInt(e.target.value, 10) || 0)} className={field} data-id="element-2124" data-mp="1e4089i1lqibq" />
                </label>
              </div>

              <div className="mt-5 flex items-center gap-3 border-t border-stone-100 pt-5" data-id="element-2125" data-mp="1spe37hul3jpy">
                <button onClick={save} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-2126" data-mp="1exzldecexokh">
                  Gem ændringer
                </button>
                {saved && <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600" data-id="element-2127" data-mp="19ywedocvh89v">
                    <CheckIcon className="h-4 w-4" data-id="element-2128" data-mp="vz58yc0f907h" />
                    Gemt
                  </span>}
              </div>
            </section>}

          {tab === 'virksomhed' && <section className={`${card} mt-5`} data-id="element-2129" data-mp="2bvzp18cuvdbo">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-2130" data-mp="2bv6dzv6v61nq">
                Data fra det gamle system
              </h2>
              <p className="mt-1 max-w-prose text-sm text-stone-500" data-id="element-2131" data-mp="22vt451etvb2h">
                Henter dine kunder og opgaver ind igen. Kunder, opgaver, leads,
                tilbud og fakturaer her bliver erstattet — det gamle system
                røres ikke, så du kan gøre det igen.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-2132" data-mp="2zm2dzflh4362">
                <button onClick={runImport} disabled={importState === 'running'} className="inline-flex items-center gap-2 rounded-lg border border-stone-300 px-4 py-2.5 text-sm font-semibold text-stone-800 transition-colors hover:bg-stone-50 disabled:text-stone-400" data-id="element-2133" data-mp="2zlag1y4eltcm">
                  <DownloadIcon className="h-4 w-4" data-id="element-2134" data-mp="3j9t2i7y0ysnm" />
                  {importState === 'running' ? 'Henter…' : 'Hent kunder og opgaver'}
                </button>
                {importState === 'done' && <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600" data-id="element-2135" data-mp="3omro0w3zl7ff">
                    <CheckIcon className="h-4 w-4" data-id="element-2136" data-mp="68ieg8r2icm8" />
                    {importCount} poster hentet
                  </span>}
                {importState === 'empty' && <span className="text-sm font-medium text-amber-600" data-id="element-2137" data-mp="39qiqbyzqv254">
                    Der ligger ingen kunder eller opgaver i det gamle system
                  </span>}
                {importState === 'error' && <span className="text-sm font-medium text-red-600" data-id="element-2138" data-mp="3ep3j5dco2jjj">
                    Det gik galt — prøv igen
                  </span>}
              </div>
            </section>}

          {tab === 'brugere' && <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-2139" data-mp="ty2zqyap1lyo">
              <ul className="divide-y divide-stone-100" data-id="element-2140" data-mp="11yivhopm8eqz">
                {employees.map(employee => <li key={employee.id} className="flex items-center gap-3 px-5 py-3.5" data-id="element-2141" data-mp="15zuj5ooxipwj">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{
                backgroundColor: employee.color
              }} data-id="element-2142" data-mp="1fzbmhzgpkrpc">
                      {employee.name.split(' ').map(part => part[0]).join('').slice(0, 2)}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-2143" data-mp="x1scotmff51v">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-2144" data-mp="3vqzhvqg2qc61">
                        {employee.name}
                      </span>
                      <span className="block truncate text-xs text-stone-500" data-id="element-2145" data-mp="4htt9twr7ah2">
                        {employee.email}
                      </span>
                    </span>
                    <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600" data-id="element-2146" data-mp="3o7hwe7j9rc6u">
                      {employee.role}
                    </span>
                  </li>)}
              </ul>
            </section>}

          {tab === 'roller' && <section className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-2147" data-mp="3a8i7qzvraxao">
              <table className="w-full min-w-[560px] text-sm" data-id="element-2148" data-mp="35ew2hsrx9vow">
                <thead data-id="element-2149" data-mp="30gkwxu8nu7ch">
                  <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-2150" data-mp="30i5nz9vrn85l">
                    <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-2151" data-mp="2vjca3gckhf4w">
                      Rettighed
                    </th>
                    {['Administrator', 'Projektleder', 'Medarbejder'].map(role => <th key={role} className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-2152" data-mp="3b7qxgitmaslr">
                          {role}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100" data-id="element-2153" data-mp="36f64tkmg6ljt">
                  {PERMISSIONS.map(permission => <tr key={permission.label} data-id="element-2154" data-mp="2xgkprzg6pons">
                      <td className="px-4 py-2.5 text-stone-700" data-id="element-2155" data-mp="2xifc9i73totz">
                        {permission.label}
                      </td>
                      {[permission.admin, permission.pm, permission.worker].map((allowed, index) => <td key={index} className="px-4 py-2.5 text-center" data-id="element-2156" data-mp="hp6itrm7u3ao" data-mpi={index}>
                            {allowed ? <CheckIcon className="mx-auto h-4 w-4 text-emerald-600" data-id="element-2157" data-mp="lq0amw2s428u" /> : <span className="text-stone-300" data-id="element-2158" data-mp="269lugfmfajxf">—</span>}
                          </td>)}
                    </tr>)}
                </tbody>
              </table>
            </section>}

          {tab === 'tilbud' && <section className={card} data-id="element-2159" data-mp="2px32caztkely">
              <label className="block" data-id="element-2160" data-mp="2pw0d07i5ak85">
                <span className={labelText} data-id="element-2161" data-mp="2pz623mqrxm3v">Tilbud gyldigt i (dage)</span>
                <input type="number" value={draft.quoteValidDays} onChange={e => set('quoteValidDays', parseInt(e.target.value, 10) || 0)} className={`${field} max-w-[200px]`} data-id="element-2162" data-mp="2kxqjs2fzdtp6" />
              </label>
              <label className="mt-4 block" data-id="element-2163" data-mp="14lthnxoj51h7">
                <span className={labelText} data-id="element-2164" data-mp="zlyr5v8o2dwo">Standardtekst nederst på tilbud</span>
                <textarea value={draft.quoteFooter} onChange={e => set('quoteFooter', e.target.value)} rows={4} className={field} data-id="element-2165" data-mp="2znoifzy4no0j" />
              </label>
              <button onClick={save} className="mt-5 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-2166" data-mp="39k1c4xtoa1va">
                Gem ændringer
              </button>
            </section>}

          {tab === 'faktura' && <section className={card} data-id="element-2167" data-mp="3sbu54s8qrn30">
              <label className="block" data-id="element-2168" data-mp="3scvhd01hufmb">
                <span className={labelText} data-id="element-2169" data-mp="3sdx4w3zp4ukx">Betalingsbetingelser (dage)</span>
                <input type="number" value={draft.paymentTermsDays} onChange={e => set('paymentTermsDays', parseInt(e.target.value, 10) || 0)} className={`${field} max-w-[200px]`} data-id="element-2170" data-mp="14zty1kbp287" />
              </label>
              <label className="mt-4 block" data-id="element-2171" data-mp="1ulvs5fc2zp5x">
                <span className={labelText} data-id="element-2172" data-mp="1po3x4cg53mxi">
                  Standardtekst nederst på faktura
                </span>
                <textarea value={draft.invoiceFooter} onChange={e => set('invoiceFooter', e.target.value)} rows={4} className={field} data-id="element-2173" data-mp="1lo3juv4s1hs1" />
              </label>
              <button onClick={save} className="mt-5 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-2174" data-mp="29deervzg4hy9">
                Gem ændringer
              </button>
            </section>}

          {tab === 'notifikationer' && <section className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-2175" data-mp="2x3ih800webno">
              <ul className="divide-y divide-stone-100" data-id="element-2176" data-mp="2saz1oxinh6g8">
                {NOTIFICATIONS.map(item => <li key={item.id} className="flex items-center gap-4 px-5 py-3.5" data-id="element-2177" data-mp="20nnoeq5vrilb">
                    <span className="min-w-0 flex-1" data-id="element-2178" data-mp="1qq9ivllhwe7s">
                      <span className="block text-sm font-medium text-stone-900" data-id="element-2179" data-mp="1qrkjxdnk37v7">
                        {item.label}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-2180" data-mp="2jex8ftda1pr5">
                        {item.hint}
                      </span>
                    </span>
                    <button role="switch" aria-checked={notifications[item.id]} aria-label={item.label} onClick={() => setNotifications(current => ({
                ...current,
                [item.id]: !current[item.id]
              }))} className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${notifications[item.id] ? 'bg-brand-600' : 'bg-stone-200'}`} data-id="element-2181" data-mp="2odi0h5idl34i">
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${notifications[item.id] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-2182" data-mp="15fc1a05v2b71" />
                    </button>
                  </li>)}
              </ul>
            </section>}

          {tab === 'integrationer' && <section className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-2183" data-mp="hq16czb6zb0t">
              {INTEGRATIONS.map(integration => <div key={integration.name} className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-4" data-id="element-2184" data-mp="3q4rnsiy1rdqv">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-2185" data-mp="177od7xmxagq3">
                    <PlugIcon className="h-[18px] w-[18px]" data-id="element-2186" data-mp="12aee1q9qeqp2" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-2187" data-mp="1g1jjj8zs6d7b">
                    <span className="block text-sm font-semibold text-stone-900" data-id="element-2188" data-mp="1g2cktufz0ai2">
                      {integration.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-stone-500" data-id="element-2189" data-mp="1pxmmui6dchvu">
                      {integration.description}
                    </span>
                  </span>
                  <span className="flex-shrink-0 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-500" data-id="element-2190" data-mp="1yzvvf2i2u16l">
                    Ikke tilsluttet
                  </span>
                </div>)}
            </section>}
        </div>
      </div>
    </div>;
}